import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { sectionErrorText, SWATCH } from '../../shared/section-form'
import {
  addSwatch,
  fromPickerValue,
  moveSwatch,
  removeSwatch,
  toPickerValue,
} from '../../shared/swatch-list'

export interface SwatchEditorProps {
  swatches: string[]
  error?: string
  onChange: (next: string[]) => void
}

export default function SwatchEditor({
  swatches,
  error,
  onChange,
}: SwatchEditorProps) {
  const { formatMessage } = useIntl()
  const [draft, setDraft] = useState('')
  const [refused, setRefused] = useState<'invalid' | 'duplicate' | undefined>(
    undefined,
  )

  const draftIsValid = SWATCH.test(draft)
  const add = () => {
    const result = addSwatch(swatches, draft)
    if (!result.ok) {
      setRefused(result.reason)

      return
    }
    setRefused(undefined)
    onChange(result.swatches)
    setDraft('')
  }

  const setDraftFrom = (raw: string) => {
    setRefused(undefined)
    setDraft(raw.replace(/^#/, '').toUpperCase())
  }

  const move = (index: number, direction: 'earlier' | 'later') =>
    onChange(moveSwatch(swatches, index, direction))

  // Only real problems go in helperText: this repo's theme paints every helper as a red pill, so
  // a hint or a blank spacer there reads as a validation failure.
  const draftHelper = () => {
    if (refused === 'duplicate') {
      return formatMessage(
        { id: 'avatarStudio.editor.swatches.duplicate' },
        { hex: draft.toUpperCase() },
      )
    }
    if (refused === 'invalid' || (draft !== '' && !draftIsValid)) {
      return formatMessage({ id: 'avatarStudio.editor.swatches.format' })
    }

    return undefined
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" component="h3">
        {formatMessage({ id: 'avatarStudio.editor.swatches' })}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {formatMessage({ id: 'avatarStudio.editor.swatches.orderMatters' })}
      </Typography>

      {swatches.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'avatarStudio.editor.swatches.empty' })}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {swatches.map((swatch, i) => (
          <Stack
            key={`${swatch}-${i}`}
            direction="row"
            spacing={0.5}
            alignItems="center"
            data-testid={`swatch-row-${swatch}`}
          >
            <Box
              role="img"
              aria-label={formatMessage(
                { id: 'avatarStudio.editor.swatches.aria' },
                { hex: swatch },
              )}
              sx={{
                width: 24,
                height: 24,
                borderRadius: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: `#${swatch}`,
              }}
            />
            <Typography variant="caption" translate="no" sx={{ mx: 0.5 }}>
              {swatch}
            </Typography>
            <IconButton
              size="small"
              disabled={i === 0}
              aria-label={formatMessage(
                { id: 'avatarStudio.editor.swatches.moveEarlier' },
                { hex: swatch },
              )}
              onClick={() => move(i, 'earlier')}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
            </IconButton>
            <IconButton
              size="small"
              disabled={i === swatches.length - 1}
              aria-label={formatMessage(
                { id: 'avatarStudio.editor.swatches.moveLater' },
                { hex: swatch },
              )}
              onClick={() => move(i, 'later')}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label={formatMessage(
                { id: 'avatarStudio.editor.swatches.remove' },
                { hex: swatch },
              )}
              onClick={() => onChange(removeSwatch(swatches, i))}
            >
              <DeleteOutlineIcon fontSize="inherit" />
            </IconButton>
          </Stack>
        ))}
      </Box>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Tooltip title={formatMessage({ id: 'avatarStudio.editor.swatches.pick' })}>
          <Box
            component="input"
            type="color"
            aria-label={formatMessage({ id: 'avatarStudio.editor.swatches.pick' })}
            value={toPickerValue(draft)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDraftFrom(fromPickerValue(e.target.value))
            }
            data-testid="swatch-picker"
            sx={{
              width: 44,
              height: 40,
              mt: 0.5,
              p: 0.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              cursor: 'pointer',
            }}
          />
        </Tooltip>
        <TextField
          size="small"
          label={formatMessage({ id: 'avatarStudio.editor.swatches.add' })}
          value={draft}
          onChange={(e) => setDraftFrom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          error={Boolean(refused) || (draft !== '' && !draftIsValid)}
          helperText={draftHelper()}
          FormHelperTextProps={{ role: 'alert' }}
          inputProps={{ maxLength: 7, spellCheck: false }}
          sx={{ width: 180 }}
        />
        <Button onClick={add} disabled={!draftIsValid} sx={{ mt: 0.5 }}>
          {formatMessage({ id: 'avatarStudio.editor.swatches.addButton' })}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error">
          {sectionErrorText(error, (id) => formatMessage({ id }))}
        </Alert>
      )}
    </Stack>
  )
}
